/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IColumn, Icon, Link, SelectionMode, Stack } from '@fluentui/react';
import orderBy from 'lodash.orderby';
import { observer } from 'mobx-react';
import React from 'react';
import { defineMessages } from 'react-intl';
import { useHistory, useParams } from 'react-router';
import { EmptyStateView } from 'src/core/components/EmptyStateView';
import { LuisText } from 'src/core/components/LuisText';
import { RBACTooltip } from 'src/core/components/RBACTooltip';
import { Table } from 'src/core/components/Table';
import { SortKeyType } from 'src/core/components/TableProps';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { getPortalFromUrl } from 'src/core/utils/routingUtils';
import { createKeyedStringNormalizer } from 'src/core/utils/strings';
import { CapabilityType } from 'src/languageStudio/core/types';
import { ModelGuidanceCardMessages } from 'src/modules/modelGuidance/components/modelGuidanceCardMessages';
import { DistributionView } from 'src/modules/modelGuidance/components/tabs/DataDistributionTab';
import { SchemaType, getRouteToTraining, handleGetLabel } from 'src/modules/modelGuidance/modelGuidanceUtils/modelGuidanceUtils';

import styled from 'styled-components';

type DataDistributionTableProps = {
    pivotName?: SchemaType;
    isExtractionProject: boolean;
    insufficientExamples: string[];
    capabilityName: CapabilityType;
    documentsDistribution: DistributionView[];
    totalLabelsDistribution: DistributionView[];
};

type DistributionTableRow = {
    labelName: string;
    totalInstances: number;
    taggedTestFiles: number;
    totalTaggedFiles: number;
    instancesInTestSet: number;
    taggedTrainingFiles: number;
    instancesInTrainingSet: number;
};

const messages = defineMessages({
    class: { id: 'modelGuidance.dataDistributionTable.class', defaultMessage: 'Class' },
    entity: { id: 'modelGuidance.dataDistributionTable.entity', defaultMessage: 'Entity' },
    intent: { id: 'modelGuidance.dataDistributionTable.intent', defaultMessage: 'Intent' },
    documents: { id: 'modelGuidance.dataDistributionTable.documents', defaultMessage: 'documents' },
    utterances: { id: 'modelGuidance.dataDistributionTable.utterances', defaultMessage: 'utterances' },
    labelName: { id: 'modelGuidance.dataDistributionTable.labelName', defaultMessage: '{labelType} name' },
    totalInstances: { id: 'modelGuidance.dataDistributionTable.totalInstances', defaultMessage: 'Tags per {labelType} total' },
    totalTaggedFiles: { id: 'modelGuidance.dataDistributionTable.totalTaggedFiles', defaultMessage: 'Tagged {fileType} total' },
    insufficientLabel: { id: 'modelGuidance.dataDistributionTable.insufficientLabel', defaultMessage: 'Not enough in training set' },
    testInstances: { id: 'modelGuidance.dataDistributionTable.testInstances', defaultMessage: 'Tags per {labelType} in testing set' },
    taggedTestFiles: { id: 'modelGuidance.dataDistributionTable.taggedTestFiles', defaultMessage: 'Tagged {fileType} in testing set' },
    taggedTrainingFiles: {
        id: 'modelGuidance.dataDistributionTable.taggedTrainingFiles',
        defaultMessage: 'Tagged {fileType} in training set'
    },
    trainingInstances: {
        id: 'modelGuidance.dataDistributionTable.trainingInstances',
        defaultMessage: 'Tags per {labelType} in training set'
    }
});

const theme = getScopedTheme('DataDistributionTable');

const NotEnoughContainer = styled(Stack)({ marginLeft: '5px' });
const NotEnoughIcon = styled(Icon)({ color: theme.warningColor });
const NotEnoughText = styled(LuisText)({
    fontWeight: 400,
    fontSize: '10px',
    lineHeight: '12px',
    color: theme.warningColor
});

export const DataDistributionTable = observer((props: DataDistributionTableProps) => {
    const {
        pivotName,
        capabilityName,
        isExtractionProject,
        documentsDistribution,
        totalLabelsDistribution,
        insufficientExamples = []
    } = props;

    const history = useHistory();
    const { localize } = useLocalization();
    const { rootStore } = useGlobalState();
    const { serviceName } = getPortalFromUrl();
    const { projectId } = useParams<{ projectId: string }>();

    const { userRole, displayedUserRole } = rootStore.serviceKey;
    const { canWrite } = mapUserRoleToUserActions(userRole);

    const routeToTraining = () => {
        history.push(getRouteToTraining(capabilityName, projectId));
    };

    const [sortKey, setSortKey] = React.useState<SortKeyType<DistributionTableRow>>({ key: 'labelName', order: 'asc' });

    const labelType = React.useMemo(() => handleGetLabel(pivotName, serviceName, false), [isExtractionProject, pivotName]);

    const fileType = React.useMemo(() => {
        if (capabilityName === 'Conversation' || capabilityName === 'Orchestration') {
            return localize(messages.utterances);
        }

        return localize(messages.documents);
    }, [capabilityName]);

    const capitalizedLabelName = React.useMemo(
        () =>
            isExtractionProject || pivotName === 'Entities' ? messages.entity : pivotName === 'Intents' ? messages.intent : messages.class,
        [isExtractionProject, pivotName]
    );

    const tableItems: DistributionTableRow[] = React.useMemo(
        () =>
            documentsDistribution?.map(dist => {
                const docDistribution: DistributionView = totalLabelsDistribution?.find(doc => doc.labelName === dist.labelName);

                return {
                    labelName: dist.labelName,
                    taggedTestFiles: dist.testingCount,
                    taggedTrainingFiles: dist.trainingCount,
                    instancesInTestSet: docDistribution?.testingCount ?? 0,
                    totalTaggedFiles: dist.testingCount + dist.trainingCount,
                    instancesInTrainingSet: docDistribution?.trainingCount ?? 0,
                    totalInstances: docDistribution?.testingCount + docDistribution?.trainingCount ?? 0
                };
            }),
        [documentsDistribution, totalLabelsDistribution]
    );

    const sortedItems = React.useMemo(() => orderBy(tableItems, [createKeyedStringNormalizer(sortKey.key)], [sortKey.order]), [
        tableItems,
        sortKey.key,
        sortKey.order
    ]);

    const isLabelInsufficient = (labelName: string) => insufficientExamples.includes(labelName);

    const onSortColumn = (column: IColumn) =>
        setSortKey({ key: column.key as keyof DistributionTableRow, order: column.isSortedDescending ? 'asc' : 'desc' });

    const columns: IColumn[] = [
        {
            minWidth: 120,
            maxWidth: 300,
            key: 'labelName',
            isRowHeader: true,
            isResizable: true,
            isCollapsible: false,
            fieldName: 'labelName',
            isSorted: sortKey?.key === 'labelName',
            name: localize(messages.labelName, { labelType: localize(capitalizedLabelName) }),
            isSortedDescending: sortKey?.key === 'labelName' && sortKey?.order === 'desc',
            onRender: (item: DistributionTableRow) => <LuisText>{item.labelName}</LuisText>
        },
        {
            minWidth: 180,
            maxWidth: 300,
            isRowHeader: true,
            isResizable: true,
            isCollapsible: false,
            key: 'taggedTrainingFiles',
            fieldName: 'taggedTrainingFiles',
            name: localize(messages.taggedTrainingFiles, { fileType }),
            isSorted: sortKey?.key === 'taggedTrainingFiles',
            isSortedDescending: sortKey?.key === 'taggedTrainingFiles' && sortKey?.order === 'desc',
            onRender: (item: DistributionTableRow) => (
                <Stack horizontal verticalAlign="center" tokens={{ childrenGap: '5px' }}>
                    {item.taggedTrainingFiles}
                    {isLabelInsufficient(item.labelName) && (
                        <NotEnoughContainer horizontal tokens={{ childrenGap: '5px' }}>
                            <NotEnoughIcon iconName="warning" />
                            <NotEnoughText>{localize(messages.insufficientLabel)}</NotEnoughText>
                        </NotEnoughContainer>
                    )}
                </Stack>
            )
        },
        {
            minWidth: 180,
            maxWidth: 300,
            isRowHeader: true,
            isResizable: true,
            isCollapsible: false,
            key: 'taggedTestFiles',
            fieldName: 'taggedTestFiles',
            name: localize(messages.taggedTestFiles, { fileType }),
            isSorted: sortKey?.key === 'taggedTestFiles',
            isSortedDescending: sortKey?.key === 'taggedTestFiles' && sortKey?.order === 'desc',
            onRender: (item: DistributionTableRow) => <LuisText>{item.taggedTestFiles}</LuisText>
        },
        {
            minWidth: 150,
            maxWidth: 300,
            isRowHeader: true,
            isResizable: true,
            isCollapsible: false,
            key: 'totalTaggedFiles',
            fieldName: 'totalTaggedFiles',
            name: localize(messages.totalTaggedFiles, { fileType }),
            isSorted: sortKey?.key === 'totalTaggedFiles',
            isSortedDescending: sortKey?.key === 'totalTaggedFiles' && sortKey?.order === 'desc',
            onRender: (item: DistributionTableRow) => <LuisText>{item.totalTaggedFiles}</LuisText>
        }
    ];

    if (totalLabelsDistribution && !pivotName) {
        columns.splice(1, 0, {
            minWidth: 180,
            maxWidth: 300,
            isRowHeader: true,
            isResizable: true,
            isCollapsible: false,
            key: 'instancesInTrainingSet',
            fieldName: 'instancesInTrainingSet',
            isSorted: sortKey?.key === 'instancesInTrainingSet',
            name: localize(messages.trainingInstances, { labelType: localize(labelType) }),
            isSortedDescending: sortKey?.key === 'instancesInTrainingSet' && sortKey?.order === 'desc',
            onRender: (item: DistributionTableRow) => <LuisText>{item.instancesInTrainingSet}</LuisText>
        });

        columns.splice(3, 0, {
            minWidth: 180,
            maxWidth: 300,
            isRowHeader: true,
            isResizable: true,
            isCollapsible: false,
            key: 'instancesInTestSet',
            fieldName: 'instancesInTestSet',
            isSorted: sortKey?.key === 'instancesInTestSet',
            name: localize(messages.testInstances, { labelType: localize(labelType) }),
            isSortedDescending: sortKey?.key === 'instancesInTestSet' && sortKey?.order === 'desc',
            onRender: (item: DistributionTableRow) => <LuisText>{item.instancesInTestSet}</LuisText>
        });

        columns.splice(5, 0, {
            minWidth: 150,
            maxWidth: 300,
            isRowHeader: true,
            isResizable: true,
            isCollapsible: false,
            key: 'totalInstances',
            fieldName: 'totalInstances',
            isSorted: sortKey?.key === 'totalInstances',
            name: localize(messages.totalInstances, { labelType: localize(labelType) }),
            isSortedDescending: sortKey?.key === 'totalInstances' && sortKey?.order === 'desc',
            onRender: (item: DistributionTableRow) => <LuisText>{item.totalInstances}</LuisText>
        });
    }

    return documentsDistribution ? (
        <Table<DistributionTableRow> columns={columns} items={sortedItems} onSortColumn={onSortColumn} selectionMode={SelectionMode.none} />
    ) : (
        <EmptyStateView
            message={
                <>
                    {localize(ModelGuidanceCardMessages.distributionEmptyState1)}
                    &nbsp;
                    <RBACTooltip
                        hidden={canWrite}
                        displayedUserRole={displayedUserRole}
                        data-automation-id="trainModelLinkLanguageToolTip"
                        componentSurroundedByToolTip={
                            <Link disabled={!canWrite} onClick={routeToTraining} styles={{ root: { color: theme.emptyStateLinkColor } }}>
                                {localize(ModelGuidanceCardMessages.retrain)}
                            </Link>
                        }
                    />
                    &nbsp;
                    {localize(ModelGuidanceCardMessages.distributionEmptyState2)}
                </>
            }
        />
    );
});
